/**
 * CapabilityDecision normalization SSOT (WL1-cr / CR-7).
 *
 * Maps partial adapter or API payloads onto the canonical full shape.
 *
 * @see doc/system-completion-plan/01-target/CAPABILITY_RESOLVER_SSOT.md §3 · §11
 */

import type { CapabilityDecision, CapabilitySource } from './capability-decision';

/** Partial decision at adapter or API boundary — `allowed` is required */
export type PartialCapabilityDecision = Partial<CapabilityDecision> &
  Pick<CapabilityDecision, 'allowed'>;

export interface NormalizeCapabilityDecisionOptions {
  /** Default when partial omits source (adapter deny uses adapter name) */
  source?: CapabilitySource;
}

/**
 * Normalizes a partial decision to full `CapabilityDecision`.
 * CR-7: `requiredPermissions` defaults to `[]`; `source` defaults to `composite`.
 */
export function normalizeCapabilityDecision(
  partial: PartialCapabilityDecision,
  options: NormalizeCapabilityDecisionOptions = {}
): CapabilityDecision {
  return {
    allowed: partial.allowed,
    requiredPermissions: partial.requiredPermissions ?? [],
    source: partial.source ?? options.source ?? 'composite',
    sensitive: partial.sensitive ?? false,
    ...(partial.reason !== undefined ? { reason: partial.reason } : {}),
    ...(partial.suggestedAction !== undefined ? { suggestedAction: partial.suggestedAction } : {}),
    ...(partial.denyReason !== undefined ? { denyReason: partial.denyReason } : {}),
  };
}
